import React, { lazy, Suspense } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary';

// Lazy load the main component
const CollectionsManagement = lazy(() => 
  import('./CollectionsManagement').then(m => ({ default: m.CollectionsManagement }))
);

/**
 * Loading skeleton for Collections page
 */
const CollectionsLoadingSkeleton = () => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>
    </div>

    {/* Tabs skeleton */}
    <Skeleton className="h-10 w-80" />

    {/* Filters skeleton */}
    <div className="space-y-4 p-4 bg-card rounded-lg border">
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    </div>

    {/* Summary card skeleton */}
    <Skeleton className="h-24 w-full rounded-lg" />

    {/* Table skeleton */}
    <div className="border rounded-lg">
      <div className="p-4 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  </div>
);

/**
 * OptimizedCollectionsManagement
 * Wrapper with lazy loading and error boundary
 */
export const OptimizedCollectionsManagement: React.FC = () => {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<CollectionsLoadingSkeleton />}>
        <CollectionsManagement />
      </Suspense>
    </AppErrorBoundary>
  );
};
